import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BaseCoreState } from '../reducers';
import { BaseRelatedCustomerLocationsService } from '../../modules/related-customer-locations/base-related-customer-locations.service';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { baseRelatedCustomerLocationsActions, baseSharedActions } from '../actions';
import { baseAuthSelectors } from '../selectors';
import { BaseFileDownloadService } from '../../shared/services/base-file-download.service';

@Injectable()
export class BaseRelatedCustomerLocationsEffects {
  loadRelatedCustomerLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadRelatedCustomerLocations),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .relatedCustomerLocationList({
            ...action.payload,
            psp: user.organizationId,
          })
          .pipe(
            mergeMap((response) => [
              baseRelatedCustomerLocationsActions.setRelatedCustomerLocations({ payload: response }),
              baseRelatedCustomerLocationsActions.successRelatedCustomerLocations(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorRelatedCustomerLocations()))
          );
      })
    );
  });

  loadRelatedCustomerLocationDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadRelatedCustomerLocationDetails),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .relatedCustomerLocationDetails({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedCustomerLocationsActions.setRelatedCustomerLocationDetails({ payload: response }),
              baseRelatedCustomerLocationsActions.successRelatedCustomerLocationDetails(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorRelatedCustomerLocationDetails()))
          );
      })
    );
  });

  loadRelatedCustomerLocationDocuments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadRelatedCustomerLocationDocuments),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .documentsList({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedCustomerLocationsActions.setRelatedCustomerLocationDocuments({ payload: response }),
              baseRelatedCustomerLocationsActions.successRelatedCustomerLocationDocuments(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorRelatedCustomerLocationDocuments()))
          );
      })
    );
  });

  downloadRelatedCustomerLocationDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.downloadRelatedCustomerLocationDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.relatedCustomerLocationsService.downloadDocument({
          ...action.payload,
          psp: user.organizationId,
        });

        return this.fileDownloadService
          .downloadFile(source$, {
            isPreview: action.payload?.openInModal,
            documentType: action.payload.documentType,
          })
          .pipe(
            mergeMap((blob) => {
              const actions: Action[] = [
                baseRelatedCustomerLocationsActions.successDownloadRelatedCustomerLocationDocument(),
              ];

              if (blob) {
                actions.unshift(
                  baseRelatedCustomerLocationsActions.setRelatedCustomerLocationDocument({
                    payload: { document: blob },
                  })
                );
              }

              return actions;
            }),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorDownloadRelatedCustomerLocationDocument()))
          );
      })
    );
  });

  loadRelatedCustomerLocationDepartments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadDepartments),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .departmentsList({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedCustomerLocationsActions.setDepartments({ payload: response }),
              baseRelatedCustomerLocationsActions.successDepartments(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorDepartments()))
          );
      })
    );
  });

  loadRelatedCustomerLocationPriceList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadRelatedCustomerLocationPriceList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .getPriceQualificationList({
            psp: user.organizationId,
            customer: action.payload.customer,
            tag: action.payload.tag,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedCustomerLocationsActions.setRelatedCustomerLocationPriceList({ payload: response }),
              baseRelatedCustomerLocationsActions.successRelatedCustomerLocationPriceList(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorRelatedCustomerLocationPriceList()))
          );
      })
    );
  });

  updateRelatedCustomerLocationPriceList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.updateRelatedCustomerLocationPriceList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .updatePriceList({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseRelatedCustomerLocationsActions.loadRelatedCustomerLocationPriceList({
                payload: {
                  customer: action.payload.customer,
                  location: action.payload.location,
                  tag: action.payload.tag ? action.payload.tag : undefined,
                },
              }),
              baseSharedActions.loadPspPriceList({
                payload: {},
              }),
            ])
          );
      })
    );
  });

  loadRelatedCustomerLocationSurcharges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadSurcharges),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .surchargesList({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedCustomerLocationsActions.setSurcharges({ payload: response }),
              baseRelatedCustomerLocationsActions.successSurcharges(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorSurcharges()))
          );
      })
    );
  });

  loadDepartmentUserList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadDepartmentUserList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .departmentUserList({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
            department: action.payload.department,
            search: action.payload.search,
          })
          .pipe(
            mergeMap((response) => [
              baseRelatedCustomerLocationsActions.setDepartmentUserList({ payload: response }),
              baseRelatedCustomerLocationsActions.successDepartmentUserList(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorDepartmentUserList()))
          );
      })
    );
  });

  loadQualificationWithPrice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedCustomerLocationsActions.loadQualificationWithPrice),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedCustomerLocationsService
          .getQualificationPrice({
            psp: user.organizationId,
            customer: action.payload.customer,
            location: action.payload.location,
            qualification: action.payload.qualification,
          })
          .pipe(
            mergeMap((response) => [
              baseRelatedCustomerLocationsActions.setQualificationWithPrice({ payload: response }),
              baseRelatedCustomerLocationsActions.successQualificationWithPrice(),
            ]),
            catchError(() => of(baseRelatedCustomerLocationsActions.errorQualificationWithPrice()))
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private relatedCustomerLocationsService: BaseRelatedCustomerLocationsService,
    private fileDownloadService: BaseFileDownloadService
  ) {}
}
